import { useState, useCallback } from "react";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import { MiniLoader } from "@/components/shared/MiniLoader";
import { useAssetUrl } from "@/hooks/loras/useAssetUrl";

// Cast is characters only. Props and locations will have their own homes.
export type CastType = "character";

interface CastCardProps {
  id: string;
  name: string;
  type: CastType;
  description?: string;
  /** Pre-resolved image URL. Takes precedence over `thumbnailAssetId`. */
  imageUrl?: string | null;
  /** LoRA preview asset id; resolved to a signed URL when no `imageUrl`. */
  thumbnailAssetId?: string | null;
  onClick: () => void;
}

// Character color (cast = characters only)
const CHARACTER_COLOR = {
  color: "var(--color-primitive-pink-500)",
  rgb: "var(--color-primitive-pink-500-rgb)",
};

export function CastCard({
  name,
  type,
  imageUrl,
  thumbnailAssetId,
  onClick,
}: CastCardProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const handleImgError = useCallback(() => setImgFailed(true), []);
  const handleImgLoad = useCallback(() => setImgLoaded(true), []);

  const { data: resolvedUrl } = useAssetUrl(imageUrl ? null : thumbnailAssetId);
  const effectiveImageUrl = imageUrl ?? resolvedUrl ?? null;

  const { color: typeColor, rgb: typeRgb } = CHARACTER_COLOR;

  return (
    <div
      className="group cursor-pointer"
      style={
        { "--type-color": typeColor, maxWidth: "240px" } as React.CSSProperties
      }
      onClick={onClick}
    >
      {/* Card container - portrait aspect ratio with integrated name panel */}
      <div
        className={cn(
          "w-full aspect-[3/4] rounded overflow-hidden transition-all duration-200 relative flex flex-col",
          "bg-card border border-border",
          "hover:shadow-lg cast-card-hover",
        )}
      >
        {/* Image area - 75% height */}
        <div className="relative overflow-hidden" style={{ flex: "0 0 75%" }}>
          {/* Cast thumbnail or placeholder */}
          {effectiveImageUrl && !imgFailed ? (
            <>
              {!imgLoaded && (
                <div className="absolute inset-0 bg-muted/50 flex items-center justify-center">
                  <MiniLoader size={24} />
                </div>
              )}
              <img
                src={effectiveImageUrl}
                alt={name}
                loading="lazy"
                decoding="async"
                className={cn(
                  "w-full h-full object-cover group-hover:scale-105 transition-all duration-300 pointer-events-none select-none",
                  !imgLoaded && "opacity-0",
                )}
                draggable={false}
                onLoad={handleImgLoad}
                onError={handleImgError}
              />
            </>
          ) : (
            // Fallback gradient with icon
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                // Preserve the former hex alpha bytes (0x20 and 0x10).
                background: `linear-gradient(135deg, rgba(${typeRgb}, calc(32 / 255)), rgba(${typeRgb}, calc(16 / 255)))`,
              }}
            >
              <User className="w-12 h-12 opacity-40" color={typeColor} />
            </div>
          )}
        </div>

        {/* Name panel - 25% height */}
        <div
          className="px-3 flex flex-col justify-center text-left"
          style={{
            flex: "0 0 25%",
            backgroundColor: "var(--color-semantic-surface-thumbnailpanel)",
          }}
        >
          <span
            className={cn(
              "text-sm font-medium transition-colors line-clamp-1 block",
              "text-foreground group-hover:text-primary cast-card-text",
            )}
          >
            {name}
          </span>
          <span
            className="text-[10px] font-semibold uppercase tracking-wider block mt-0.5"
            style={{ color: typeColor }}
          >
            {type}
          </span>
        </div>
      </div>
    </div>
  );
}
